import { useCallback, useEffect, useState } from 'react';
import AdminHeader from '../../components/AdminHeader';

type Candidato = {
  ref_id: string;
  cliente_nome?: string | null;
  nome_homenageado?: string | null;
  cliente_phone?: string | null;
  valor: number;
  score: number;
  idade_minutos: number;
  breakdown?: { valor?: number; nome?: number; tempo?: number };
};

type Pendente = {
  id: number;
  asaas_payment_id: string;
  valor: number;
  pagador_nome: string | null;
  pagador_cpf: string | null;
  candidatos_pedidos: Candidato[] | null;
  status: string;
  criado_em: string;
};

type Orfao = {
  id: number;
  asaas_payment_id: string;
  valor: number;
  pagador_nome: string | null;
  pagador_cpf: string | null;
  status: string;
  observacao: string | null;
  criado_em: string;
};

type Metrics = {
  auto: number;
  comprovante: number;
  manual_admin: number;
  pendentes: number;
  orfaos: number;
  total_24h: number;
  pct_auto: number;
  pct_comprovante: number;
  pct_orfao: number;
};

type ApiData = {
  pendentes: Pendente[];
  orfaos: Orfao[];
  metrics: Metrics;
};

const fmtMoney = (v: number) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const tempoDesde = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export default function PixPendentes() {
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch('/api/admin-pix?mode=all', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const resolverPendente = async (id: number, ref_id: string) => {
    if (busy) return;
    if (!confirm(`Atribuir esse Pix ao pedido ${ref_id}? A geração da música será disparada automaticamente.`)) return;
    setBusy(true);
    try {
      const res = await fetch('/api/admin-pix?target=pendente', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id, ref_id })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      await load();
    } catch (e: any) {
      alert(`Falha: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const resolverOrfao = async (id: number, action: 'atribuir' | 'ignorar' | 'estornado', ref_id?: string) => {
    if (busy) return;
    let observacao = '';
    if (action === 'atribuir') {
      if (!ref_id) {
        const input = prompt('ref_id do pedido para atribuir este PIX:');
        if (!input) return;
        ref_id = input.trim();
      }
      if (!confirm(`Atribuir este Pix órfão ao pedido ${ref_id}?`)) return;
    } else if (action === 'estornado') {
      observacao = prompt('Observação (motivo do estorno):') || '';
      if (!confirm(`Marcar como estornado? (você precisa fazer o estorno manualmente no painel Asaas)`)) return;
    } else if (action === 'ignorar') {
      observacao = prompt('Observação (motivo do ignorar):') || '';
      if (!confirm('Ignorar este PIX órfão?')) return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/admin-pix?target=orfao', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id, action, ref_id, observacao })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      await load();
    } catch (e: any) {
      alert(`Falha: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-shell" style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <AdminHeader title="Admin · PIX Pendentes" />

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 6px 0' }}>
          PIX pendentes de conciliação
        </h1>
        <p style={{ color: 'var(--text-muted)', margin: '0 0 24px 0' }}>
          Pagamentos PIX recebidos sem identificação automática. Auto-refresh a cada 30s.
        </p>

        {error && (
          <div style={{ padding: 12, background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', borderRadius: 12, marginBottom: 16, color: '#a93232' }}>
            {error}
          </div>
        )}

        {loading && !data && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
        )}

        {data && (
          <>
            <Metrics metrics={data.metrics} />

            <Section title={`Aguardando comprovante (${data.pendentes.length})`} hint="Score 50-79 ou empate — cliente foi avisado, aguardando envio do comprovante.">
              {data.pendentes.length === 0 ? (
                <Empty msg="Nenhum PIX aguardando comprovante" />
              ) : (
                data.pendentes.map((p) => (
                  <PendenteCard key={p.id} pendente={p} onResolve={resolverPendente} busy={busy} />
                ))
              )}
            </Section>

            <Section title={`PIX órfãos (${data.orfaos.length})`} hint="Sem pedido aberto compatível — exigem decisão manual.">
              {data.orfaos.length === 0 ? (
                <Empty msg="Nenhum PIX órfão pendente" />
              ) : (
                data.orfaos.map((o) => (
                  <OrfaoCard key={o.id} orfao={o} onResolve={resolverOrfao} busy={busy} />
                ))
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────

function Metrics({ metrics }: { metrics: Metrics }) {
  const items = [
    { label: 'Match automático', value: `${metrics.pct_auto}%`, count: metrics.auto, color: 'var(--gold)' },
    { label: 'Via comprovante', value: `${metrics.pct_comprovante}%`, count: metrics.comprovante, color: '#1e6fa8' },
    { label: 'Manual admin', value: '—', count: metrics.manual_admin, color: 'var(--text-muted)' },
    { label: 'Aguardando', value: '—', count: metrics.pendentes, color: '#cc6620' },
    { label: 'Órfãos', value: `${metrics.pct_orfao}%`, count: metrics.orfaos, color: '#a93232' }
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
      {items.map((m) => (
        <div key={m.label} className="admin-glass" style={{
          padding: 14, background: 'var(--surface-white)', borderRadius: 16
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {m.label}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: m.color, marginTop: 4 }}>{m.value}</div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 2 }}>{m.count} nas últimas 24h</div>
        </div>
      ))}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px 0' }}>{title}</h2>
      {hint && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px 0' }}>{hint}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </section>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--warm-white)', borderRadius: 12, border: '1px dashed var(--border-soft)' }}>
      ✓ {msg}
    </div>
  );
}

function PendenteCard({ pendente, onResolve, busy }: { pendente: Pendente; onResolve: (id: number, ref_id: string) => void; busy: boolean }) {
  const [expanded, setExpanded] = useState(true);
  const cands = pendente.candidatos_pedidos || [];
  return (
    <div className="admin-glass" style={{ padding: 16, background: 'var(--surface-white)', borderRadius: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            {fmtMoney(pendente.valor)} — {pendente.pagador_nome || '(sem nome)'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {pendente.asaas_payment_id} · {pendente.pagador_cpf || '—'} · há {tempoDesde(pendente.criado_em)} ({fmtDateTime(pendente.criado_em)})
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} style={btnStyle('secondary')}>
          {expanded ? 'Ocultar candidatos' : `Mostrar ${cands.length} candidatos`}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cands.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nenhum candidato registrado.</div>
          ) : (
            cands.map((c, i) => (
              <div key={c.ref_id} style={{
                padding: 12, background: i === 0 ? 'var(--badge-bg)' : 'var(--warm-white)',
                border: '1px solid var(--border-soft)', borderRadius: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {c.cliente_nome || '(sem nome)'} · {c.nome_homenageado ? `pra ${c.nome_homenageado}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    <code>{c.ref_id}</code> · {fmtMoney(c.valor)} · {c.idade_minutos}min atrás · score {c.score}
                    {c.breakdown && ` (val:${c.breakdown.valor}, nome:${c.breakdown.nome}, tempo:${c.breakdown.tempo})`}
                  </div>
                </div>
                <button
                  disabled={busy}
                  onClick={() => onResolve(pendente.id, c.ref_id)}
                  style={btnStyle('primary')}
                >
                  Atribuir a este
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function OrfaoCard({ orfao, onResolve, busy }: { orfao: Orfao; onResolve: (id: number, action: 'atribuir' | 'ignorar' | 'estornado', ref_id?: string) => void; busy: boolean }) {
  return (
    <div className="admin-glass" style={{ padding: 16, background: 'var(--surface-white)', borderRadius: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            {fmtMoney(orfao.valor)} — {orfao.pagador_nome || '(sem nome)'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {orfao.asaas_payment_id} · {orfao.pagador_cpf || '—'} · há {tempoDesde(orfao.criado_em)} ({fmtDateTime(orfao.criado_em)})
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button disabled={busy} onClick={() => onResolve(orfao.id, 'atribuir')} style={btnStyle('primary')}>
            Atribuir a pedido
          </button>
          <button disabled={busy} onClick={() => onResolve(orfao.id, 'estornado')} style={btnStyle('secondary')}>
            Estornar
          </button>
          <button disabled={busy} onClick={() => onResolve(orfao.id, 'ignorar')} style={btnStyle('ghost')}>
            Ignorar
          </button>
        </div>
      </div>
    </div>
  );
}

function btnStyle(variant: 'primary' | 'secondary' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    border: '1px solid',
    transition: 'opacity 0.15s'
  };
  if (variant === 'primary') return { ...base, background: 'var(--gold)', color: 'var(--gold-ink)', borderColor: 'var(--gold)' };
  if (variant === 'secondary') return { ...base, background: 'var(--surface-white)', color: 'var(--text-strong)', borderColor: 'var(--border-soft)' };
  return { ...base, background: 'transparent', color: 'var(--text-muted)', borderColor: 'transparent' };
}
